import { CHANNEL_KINDS, type ChannelKind } from '@/lib/api/channel-connections';
import bigCommerceLogo from '@/assets/shops/BigCommerce.svg?url';
import shopifyLogo from '@/assets/shops/Shopify.svg?url';
import magentoLogo from '@/assets/shops/magento.svg?url';
import wooCommerceLogo from '@/assets/shops/WooCommerce.svg?url';
import amazonLogo from '@/assets/shops/Amazon.svg?url';
import ebayLogo from '@/assets/shops/EBay.svg?url';
import etsyLogo from '@/assets/shops/Etsy.svg?url';
import nothsLogo from '@/assets/shops/Not_On_The_High_Street.svg?url';
import squarespaceLogo from '@/assets/shops/Squarespace.svg?url';
import wixLogo from '@/assets/shops/Wix.svg?url';
import tiktokShopLogo from '@/assets/shops/TikTok_Shop.svg?url';

/**
 * Display metadata for each eCommerce platform — what the merchant
 * sees in Step 3's card grid per ADR 0016. Mirrors the carrier-side
 * `carrier-catalogue.ts`; same trade-offs apply.
 *
 * Logos are a discriminated union because not every platform has a
 * usable raster asset:
 *   - `image` covers the standard case (an SVG in `assets/shops/`).
 *     `darkBackground` flags logos that are white-on-transparent and
 *     need a dark container to be readable (e.g. TikTok).
 *   - `icon` covers cases where there's no brand mark to ship — the
 *     "Custom Integration" tile uses a Lucide glyph instead.
 */
export type ChannelLogo =
  | { readonly kind: 'image'; readonly url: string; readonly darkBackground?: true }
  | { readonly kind: 'icon' };

export interface ChannelCatalogueEntry {
  readonly displayName: string;
  readonly tagline: string;
  readonly logo: ChannelLogo;
}

export const CHANNEL_CATALOGUE: Record<ChannelKind, ChannelCatalogueEntry> = {
  bigcommerce: {
    displayName: 'BigCommerce',
    tagline: 'Install Mailchute from your BigCommerce admin to pull in orders automatically.',
    logo: { kind: 'image', url: bigCommerceLogo },
  },
  shopify: {
    displayName: 'Shopify',
    tagline: "Sync orders from your Shopify store via Shopify's app marketplace.",
    logo: { kind: 'image', url: shopifyLogo },
  },
  magento: {
    displayName: 'Magento',
    tagline: 'Connect Adobe Commerce / Magento with a REST API key.',
    logo: { kind: 'image', url: magentoLogo },
  },
  woocommerce: {
    displayName: 'WooCommerce',
    tagline: 'Pull orders from your WooCommerce store via the REST API.',
    logo: { kind: 'image', url: wooCommerceLogo },
  },
  amazon: {
    displayName: 'Amazon',
    tagline: 'Marketplace orders via Amazon Seller Central (SP-API).',
    logo: { kind: 'image', url: amazonLogo },
  },
  ebay: {
    displayName: 'eBay',
    tagline: 'Sync orders from your eBay store.',
    logo: { kind: 'image', url: ebayLogo },
  },
  etsy: {
    displayName: 'Etsy',
    tagline: 'Pull orders from your Etsy shop.',
    logo: { kind: 'image', url: etsyLogo },
  },
  notonthehighstreet: {
    displayName: 'Not on the highstreet',
    tagline: 'Sync orders from your NOTHS partner account.',
    logo: { kind: 'image', url: nothsLogo },
  },
  squarespace: {
    displayName: 'Squarespace',
    tagline: 'Orders from your Squarespace Commerce store.',
    logo: { kind: 'image', url: squarespaceLogo },
  },
  wix: {
    displayName: 'Wix',
    tagline: 'Orders from your Wix Stores site.',
    logo: { kind: 'image', url: wixLogo },
  },
  tiktokshop: {
    displayName: 'TikTok Shop',
    tagline: 'Orders from your TikTok Shop seller account.',
    logo: { kind: 'image', url: tiktokShopLogo, darkBackground: true },
  },
  custom: {
    displayName: 'Custom Integration',
    tagline: 'Bring your own endpoint — webhook, REST API, or CSV import.',
    logo: { kind: 'icon' },
  },
};

/**
 * Stable ordering for the card grid. BigCommerce first (the MVP
 * platform), then the rest in the catalogue's declared order.
 */
export const ORDERED_CHANNEL_KINDS: readonly ChannelKind[] = CHANNEL_KINDS;
